<script lang="ts" setup>
import './scss/Home.scss';
import './scss/ContratacionTerrestre.scss';
import TyC from './TyC.vue'
import { ref, computed, onMounted, ComponentPublicInstance, onBeforeUnmount } from 'vue';
import Footer from './components/Footer.vue';
import {
  findCompaniesGPS,
  createOrUpdateClient,
  createPolicy,
} from '../../../repositories/landing.repository';
import { wrongDepartureDateTime, formatterRUT } from '../../../helpers';
import storePolicy from '@store/policy';
import { useRouter } from 'vue-router';
import { BModal } from 'bootstrap-vue-next';
import GeoRefChileRepository from '../../../repositories/geoRefChile.repository';
import { IRegion } from '../../../repositories/geoRefChile.repository';
import {
  finalCalculationCH,
  IFinalCalculation,
} from '../../../helpers/finalCalculationCH';
import moment from 'moment';
import { useI18n } from 'vue-i18n'
import CaptchaCloudflare from './components/CaptchaCloudflare.vue';

type CaptchaExpose = {
  reset: () => void
}

const { t } = useI18n();
const store = storePolicy();
const router = useRouter();
const refForm2 = ref<any>(null);
const refForm3 = ref<any>(null);
const isOpenModal = ref<boolean>(false);
const isOpenModalFinish = ref<boolean>(false);
const checkedAuth = ref<boolean>(false);
const loading = ref<boolean>(false);
const policyId = ref<number | null>(1);
const error = ref<string>('');
const regionesCHI = ref<IRegion[]>([]);
const companySelectedByParams = ref<{ nombre?: string; id: number } | null>(
  null,
);
const finalCalculation = ref<IFinalCalculation | null>(null);
const tokenCaptcha = ref<string | null>(null);
const captchaRef = ref<ComponentPublicInstance<CaptchaExpose> | null>(null);
const isSmallScreen = ref<boolean>(false);
const MOBILE_BREAKPOINT = 768;
const MOBILE_HALF_WIDTH_KEYS = new Set([
  'startDate',
  'startTime',
  'endDate',
  'endTime',
  'endForm',
  'finalCalculation'
]);
const MOBILE_STOPS_KEYS = new Set(['stops', 'stop1', 'stop2', 'stop3']);

const updateScreenSize = () => {
  if (typeof window === 'undefined') return;
  isSmallScreen.value = window.innerWidth < MOBILE_BREAKPOINT;
};

onMounted(() => {
  updateScreenSize();
  window.addEventListener('resize', updateScreenSize);
  const { subAsset, product, ...rest } = store.data;

  // Validamos si hay alguna propiedad (excepto subAsset y product) que sea null
  const thereAreNullProps = Object.values(rest).some((valor) => valor === null);

  // Validamos la relación entre subAsset y product
  const isProductRequiredButMissing = subAsset !== null && product === null;

  if (thereAreNullProps || isProductRequiredButMissing) {
    router.push('/');
  }
});

onMounted(async () => {

  let updatesClientInputs: { key: string; value: any }[] = [];
  if (store.client?.tradeName) {
    updatesClientInputs = [
      { key: 'tradeName', value: store.client.tradeName },
      { key: 'phone1', value: store.client.phone1 },
      {
        key: 'companyGPSId',
        value: {
          id: companySelectedByParams.value?.id ?? store.client.companyGPS.id,
          nombre:
            companySelectedByParams.value?.nombre ??
            store.client.companyGPS.description,
        },
      },
      { key: 'address', value: store.client.address },
      { key: 'region', value: { nombre: store.client.regionProvince } },
      { key: 'comuna', value: { nombre: store.client.location } },
      { key: 'postCode', value: store.client.postCode },
    ];
  }

  updateFieldsInArray(fieldsForm2.value, updatesClientInputs, null);

  const regiones = await GeoRefChileRepository.getRegiones();

  regionesCHI.value = regiones;
  const comapaniesGPS = await findCompaniesGPS();
  fieldsForm2.value = fieldsForm2.value.map((field: any) => {
    if (field.key === 'companyGPSId') {
      field.options = comapaniesGPS;
      const gpsCompany = store.data.queryParams.gpsCompany;
      if (gpsCompany) {
        const company = comapaniesGPS.find((company) => {
          return company.slug === gpsCompany;
        });
        if (company) {
          companySelectedByParams.value = company;
          field.value = company;
          field.disabled = true;
          field.customClass =
            'multiselect--disabled .multiselect__select inputLanding';
        }
      }
    }
    if (field.key === 'region') {
      field.options = regiones;
    }

    return field;
  });
});

onBeforeUnmount(() => {
  if (typeof window === 'undefined') return;
  window.removeEventListener('resize', updateScreenSize);
});

computed(() => fieldsForm3.value);

const fieldsForm2: any = ref([
  {
    key: 'tradeName',
    title: t('landing.terrestre.contratacion_cl.nombre_del_cliente'),
    type: 'text',
    validations: {
      rules: {
        required: true,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
    placeholder: t('landing.terrestre.contratacion_cl.nombre_del_cliente'),
    value: null,
    cols: 12,
    customClass: 'inputLanding',
    formatValue: (e: string) => e?.toUpperCase() ?? '',
  },
  {
    key: 'email',
    title: t('landing.terrestre.contratacion_cl.email_del_cliente'),
    type: 'email',
    validations: {
      rules: {
        required: true,
        email: true,
      },
      messages: {
        required: t('global.es_obligatorio'),
        email: t('global.email_invalido'),
      },
    },
    placeholder: t('landing.terrestre.contratacion_cl.email_del_cliente'),
    value: store.data.email ?? null,
    cols: 12,
    customClass: 'inputLanding',
  },
  {
    key: 'phone1',
    title: t('landing.terrestre.contratacion_cl.telefono_del_cliente'),
    type: 'text',
    placeholder: t('landing.terrestre.contratacion_cl.telefono_del_cliente'),
    value: null,
    cols: 12,
    customClass: 'inputLanding',
  },
  {
    key: 'companyGPSId',
    title: t('landing.terrestre.contratacion_cl.compania_gps'),
    tooltip: true,
    tooltipMessage: 'Si no encuentra la empresa de GPS en la lista, favor contactar a XXXXXXXXX',
    selectLabel: null,
    type: 'select',
    validations: {
      rules: {
        required: true,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
    disabled: false,
    placeholder: t('landing.terrestre.contratacion_cl.empresa_gps'),
    value: null,
    cols: 12,
    customClass: 'inputSelect',
    options: [],
  },
  {
    key: 'address',
    title: t('landing.terrestre.contratacion_cl.direccion_del_cliente'),
    type: 'text',
    validations: {
      rules: {
        required: true,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
    placeholder: t('landing.terrestre.contratacion_cl.direccion_del_cliente'),
    value: null,
    cols: 12,
    customClass: 'inputLanding',
  },
  {
    key: 'postCode',
    title: t('landing.terrestre.contratacion_cl.codigo_postal'),
    type: 'text',
    validations: {
      rules: {
        required: true,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
    placeholder: t('landing.terrestre.contratacion_cl.codigo_postal_placeholder'),
    value: null,
    cols: 12,
    customClass: 'inputLanding',
  },
  {
    key: 'region',
    title: t('landing.terrestre.contratacion_cl.region'),
    selectLabel: null,
    type: 'select',
    validations: {
      rules: {
        required: true,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
    placeholder: t('landing.terrestre.contratacion_cl.region'),
    value: null,
    cols: 12,
    customClass: 'inputSelect',
    options: [],
    listenSelect: true,
  },
  {
    key: 'comuna',
    title: t('landing.terrestre.contratacion_cl.comuna'),
    type: 'select',
    validations: {
      rules: {
        required: true,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
    placeholder: t('landing.terrestre.contratacion_cl.comuna'),
    value: null,
    options: [],
    cols: 12,
    customClass: 'inputSelect',
  },
]);

const fieldsForm3: any = ref([
  { slot: true, key: 'title' },
  {
    key: 'typeOfMerchandise',
    title: t('landing.terrestre.contratacion_cl.tipo_de_mercancia'),
    type: 'text',
    validations: {
      rules: {
        required: true,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
    placeholder: t('landing.terrestre.contratacion_cl.tipo_de_mercancia'),
    value: store.data?.asset?.nombre,
    cols: 12,
    customClass: 'inputLanding',
    disabled: true,
  },
  {
    key: 'indemnificationLimit',
    title: t('landing.terrestre.contratacion_cl.limite_de_indemnizacion_en_uf'),
    type: 'number',
    validations: {
      rules: {
        required: true,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
    placeholder: t('landing.terrestre.contratacion_cl.limite_de_indemnizacion_en_uf'),
    value: store.data.indemnificationLimit,
    cols: 12,
    customClass: 'inputLanding',
    disabled: true,
  },
  {
    key: 'indemnificationLimit-calc',
    title: t('landing.terrestre.contratacion_cl.precio'),
    type: 'text',
    value: `${store.currencyType.simbol} ${store.data.endPrice} + IVA`,
    validations: {
      rules: {
        required: true,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
    placeholder: t('landing.terrestre.contratacion_cl.precio_mas_iva'),
    disabled: true,
    cols: 12,
    customClass: 'inputLanding',
  },
  {
    key: 'originRegion',
    title: t('landing.terrestre.contratacion_cl.region_de_origen'),
    type: 'select',
    validations: {
      rules: {
        required: true,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
    options: regionesCHI,
    placeholder: t('landing.terrestre.contratacion_cl.region_de_origen'),
    value: null,
    cols: 6,
    customClass: 'inputSelect',
    listenSelect: true,
  },
  {
    key: 'originComuna',
    title: t('landing.terrestre.contratacion_cl.comuna_de_origen'),
    type: 'select',
    validations: {
      rules: {
        required: true,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
    placeholder: t('landing.terrestre.contratacion_cl.comuna_de_origen'),
    value: null,
    cols: 6,
    options: [],
    customClass: 'inputSelect',
    disabled: true,
  },
  {
    key: 'destinationRegion',
    title: t('landing.terrestre.contratacion_cl.region_de_destino_final'),
    type: 'select',
    validations: {
      rules: {
        required: true,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
    placeholder: t('landing.terrestre.contratacion_cl.region_de_destino_final'),
    value: null,
    options: regionesCHI,
    cols: 6,
    customClass: 'inputSelect',
    listenSelect: true,
  },
  {
    key: 'destinationComuna',
    title: t('landing.terrestre.contratacion_cl.comuna_de_destino_final'),
    type: 'select',
    validations: {
      rules: {
        required: true,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
    placeholder: t('landing.terrestre.contratacion_cl.comuna_de_destino_final'),
    value: null,
    cols: 6,
    options: [],
    customClass: 'inputSelect',
    disabled: true,
  },
  {
    key: 'stops',
    title: t('landing.terrestre.contratacion_cl.paradas_intermedias'),
    selectLabel: null,
    type: 'select',
    placeholder: t('landing.terrestre.contratacion_cl.paradas_intermedias_placeholder'),
    value: null,
    cols: 3,
    customClass: 'inputSelect',
    validations: {
      rules: {
        required: true,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
    options: [
      { nombre: 'Si', id: 1 },
      { nombre: 'No', id: 0 },
    ],
    listenSelect: true,
  },
  {
    key: 'stop1',
    title: t('landing.terrestre.contratacion_cl.parada_1'),
    type: 'text',
    placeholder: t('landing.terrestre.contratacion_cl.parada_1'),
    value: null,
    cols: 3,
    disabled: true,
    validations: {
      rules: {
        required: false,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
    customClass: 'inputLanding inputLanding--mt',
  },
  {
    key: 'stop2',
    title: t('landing.terrestre.contratacion_cl.parada_2'),
    type: 'text',
    placeholder: t('landing.terrestre.contratacion_cl.parada_2'),
    value: null,
    cols: 3,
    disabled: true,
    customClass: 'inputLanding inputLanding--mt',
  },
  {
    key: 'stop3',
    title: t('landing.terrestre.contratacion_cl.parada_3'),
    type: 'text',
    placeholder: t('landing.terrestre.contratacion_cl.parada_3'),
    value: null,
    cols: 3,
    disabled: true,
    customClass: 'inputLanding inputLanding--mt',
  },
  {
    key: 'startDate',
    title: t('landing.terrestre.contratacion_cl.fecha_inicio_viaje'),
    type: 'datetime',
    placeholder: t('landing.terrestre.contratacion_cl.fecha_inicio_viaje'),
    value: null,
    enableTimePicker: false,
    format: 'dd/MM/yyyy',
    modelType: 'yyyy-MM-dd',
    min: new Date(),
    autoApply: true,
    cols: 6,
    listenChange: true,
    validations: {
      rules: {
        required: true,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
    customClass: 'inputLanding inputDateTime',
  },
  {
    key: 'startTime',
    title: t('landing.terrestre.contratacion_cl.hora_inicio_viaje'),
    type: 'time',
    placeholder: t('landing.terrestre.contratacion_cl.hora_inicio_viaje'),
    value: null,
    cols: 6,
    validations: {
      rules: {
        required: true,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
    customClass: 'inputLanding',
    listenFocus: true,
  },
  {
    key: 'endDate',
    title: t('landing.terrestre.contratacion_cl.fecha_fin_viaje'),
    type: 'datetime',
    placeholder: t('landing.terrestre.contratacion_cl.fecha_fin_viaje'),
    value: null,
    cols: 6,
    enableTimePicker: false,
    format: 'dd/MM/yyyy',
    modelType: 'yyyy-MM-dd',
    min: new Date(),
    disabled: true,
    autoApply: true,
    validations: {
      rules: {
        required: true,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
    customClass: 'inputLanding inputDateTime',
  },
  {
    key: 'endTime',
    title: t('landing.terrestre.contratacion_cl.hora_fin_viaje'),
    type: 'time',
    placeholder: t('landing.terrestre.contratacion_cl.hora_fin_viaje'),
    value: null,
    cols: 6,
    disabled: true,
    validations: {
      rules: {
        required: true,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
    customClass: 'inputLanding',
  },
  {
    key: 'documentDescription',
    title: t('landing.terrestre.contratacion_cl.documentos_del_viaje'),
    type: 'text',
    placeholder: t('landing.terrestre.contratacion_cl.documentos_del_viaje'),
    value: null,
    cols: 6,
    validations: {
      rules: {
        required: true,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
    customClass: 'inputLanding',
  },
  {
    key: 'document',
    title: t('landing.terrestre.contratacion_cl.adjuntar_documentos'),
    type: 'file',
    placeholder: t('landing.terrestre.contratacion_cl.adjuntar_documentos'),
    value: null,
    cols: 6,
    hidden: false,
    customClass: 'inputFile',
  },
  {
    key: 'truckPlate',
    title: t('landing.terrestre.contratacion_cl.placa_tractor'),
    type: 'text',
    placeholder: t('landing.terrestre.contratacion_cl.placa_tractor'),
    value: null,
    cols: 4,
    validations: {
      rules: {
        required: true,
        verifyExpreRegular: /^(?:[A-Za-z]{4}(?:\d{2}|\d{3}|\-\d{2}(?:-\d)?)|[A-Za-z]{2}(?:\d{4,5}|\-\d{4}(?:-\d)?))$/,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
    customClass: 'inputLanding inputLanding--fs',
  },
  {
    key: 'box-trailer',
    title: t('landing.terrestre.contratacion_cl.caja_de_carga'),
    selectLabel: null,
    type: 'select',
    placeholder: t('landing.terrestre.contratacion_cl.caja_de_carga'),
    value: null,
    cols: 4,
    options: [
      { nombre: 'Si', id: 1 },
      { nombre: 'No', id: 0 },
    ],
    validations: {
      rules: {
        required: true,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
    hidden: false,
    customClass: 'inputSelect inputLanding--fs',
    listenSelect: true,
  },
  {
    key: 'trailerPlate',
    title: t('landing.terrestre.contratacion_cl.placa_caja'),
    type: 'text',
    placeholder: t('landing.terrestre.contratacion_cl.placa_caja'),
    value: null,
    cols: 4,
    customClass: 'inputLanding inputLanding--fs',
    validations: {
      rules: {
        required: false,
        verifyExpreRegular: /^(?:[A-Za-z]{4}(?:\d{3}|\-\d{2}(?:-\d)?)|[A-Za-z]{2}(?:\d{4,5}|\-\d{4}(?:-\d)?))$/,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
    disabled: true,
  },
  {
    key: 'driverName',
    title: t('landing.terrestre.contratacion_cl.nombre_del_conductor'),
    type: 'text',
    placeholder: t('landing.terrestre.contratacion_cl.nombre_del_conductor'),
    value: null,
    cols: 12,
    validations: {
      rules: {
        required: true,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
    customClass: 'inputLanding',
  },
  {
    key: 'driverCellphone',
    title: t('landing.terrestre.contratacion_cl.movil_del_conductor'),
    type: 'text',
    placeholder: t('landing.terrestre.contratacion_cl.movil_del_conductor'),
    value: null,
    cols: 12,
    validations: {
      rules: {
        required: true,
        max: 12,
        min: 8,
      },
      messages: {
        required: t('global.es_obligatorio'),
      },
    },
    customClass: 'inputLanding',
  },
  {
    key: 'driverEmail',
    title: t('landing.terrestre.contratacion_cl.email_del_conductor'),
    type: 'email',
    placeholder: t('landing.terrestre.contratacion_cl.email_del_conductor'),
    value: null,
    cols: 12,
    validations: {
      rules: {
        required: true,
        email: true,
      },
      messages: {
        email: t('global.email_invalido'),
        required: t('global.es_obligatorio'),
      },
    },
    hidden: false,
    customClass: 'inputLanding',
  },
  { slot: true, key: 'finalCalculation', cols: 12 },
  { slot: true, key: 'endForm', cols: 12 },
]);

const responsiveFieldsForm2 = computed(() =>
  fieldsForm2.value.map((field: any) => ({
    ...field,
    cols:
      isSmallScreen.value && MOBILE_STOPS_KEYS.has(field.key)
        ? 6
        : isSmallScreen.value && !MOBILE_HALF_WIDTH_KEYS.has(field.key)
        ? 12
        : field.cols,
  })),
);

const responsiveFieldsForm3 = computed(() =>
  fieldsForm3.value.map((field: any) => ({
    ...field,
    cols:
      isSmallScreen.value && MOBILE_STOPS_KEYS.has(field.key)
        ? 6
        : isSmallScreen.value && !MOBILE_HALF_WIDTH_KEYS.has(field.key)
        ? 12
        : field.cols,
  })),
);

// Handles form changes for refForm3
const handleSelectChange = async (e: any) => {

  const updateFields: {
    key: string;
    disabled?: boolean;
    'validations.rules.required'?: boolean;
    options?: any[];
  }[] = [];

  const updateCommonFields = (key: string, isEnabled: boolean) => {
    updateFields.push(
      { key, disabled: !isEnabled },
      { key, 'validations.rules.required': isEnabled },
    );
  };

  switch (e.input.key) {
    case 'box-trailer':
      updateCommonFields('trailerPlate', !!e.value.id);
      break;

    case 'originRegion':
    case 'destinationRegion':
      const cities = await GeoRefChileRepository.getComunas(e.value.id);
      const cityKey =
        e.input.key === 'originRegion' ? 'originComuna' : 'destinationComuna';
      await refForm3.value.setValue({ key: cityKey, value: null });
      updateFieldsInArray(
        fieldsForm3.value,
        [{ key: cityKey, options: cities, disabled: false }],
        null,
      );
      if (!finalCalculation.value) {
        finalCalculation.value = finalCalculationCH(
          store.data.endPrice,
          store.pricing.uf,
        );
      }
      return;
    default:
      ['stop1', 'stop2', 'stop3'].forEach((stop) =>
        updateCommonFields(stop, !!e.value.id),
      );
      break;
  }

  updateFieldsInArray(fieldsForm3.value, updateFields, null);
};

// La funcion busca las localidades al elejir la provincia
// Al obtener de que provincia es el cliente ya podemos generar el calculo final de la poliza
const handleGetCities = async () => {
  const { items } = await refForm2.value.getValues();
  const comunas = await GeoRefChileRepository.getComunas(items.region.id);
  await refForm2.value.setValue({ key: 'comuna', value: null });

  const preservedValues = {
    tradeName: items.tradeName,
    email: items.email,
    phone1: items.phone1,
    companyGPSId: items.companyGPSId,
    address: items.address,
    region: items.region,
  };

  updateFieldsInArray(
    fieldsForm2.value,
    [{ key: 'comuna', options: comunas, value: null }],
    preservedValues,
  );

  finalCalculation.value = finalCalculationCH(
    store.data.endPrice,
    store.pricing.uf,
  );
};

const updateFieldsInArray = (
  array: any,
  updates: any,
  preservedValues: any,
) => {
  for (const update of updates) {
    const field = array.find((field: any) => field.key === update.key);
    if (field) {
      if ('type' in update) {
        field.type = update.type;
      }
      if ('options' in update) {
        field.options = update.options;
      }
      if ('value' in update) {
        field.value = update.value;
      }
      if ('disabled' in update) {
        field.disabled = update.disabled;
      }

      if ('customClass' in update) {
        field.customClass = update.customClass;
      }
    }
  }

  if (preservedValues) {
    for (const key in preservedValues) {
      const field = array.find((field: any) => field.key === key);
      if (field) {
        field.value = preservedValues[key];
      }
    }
  }
};

const handleAllowEndDate = async (e: any) => {
  const isDisabled = !e.value;
  fieldsForm3.value.find((field: any) => field.key === 'endDate').disabled = isDisabled;
  fieldsForm3.value.find((field: any) => field.key === 'endTime').disabled = isDisabled;
  fieldsForm3.value.find((field: any) => field.key === 'endDate').min = new Date(`${e.value}T00:00:00`);
  await refForm3.value.setValue({ key: 'endDate', value: null });
  await refForm3.value.setValue({ key: 'endTime', value: null });
};

// Submit form with all values
const handleOnSubmitPolicies = async ({ items, isFormValid }: any) => {
  const { items: itemsForm2, isFormValid: isFormValidForm2 } =
    await refForm2.value.getValues();

  if (!tokenCaptcha.value) {
    return (error.value = t('landing.terrestre.contratacion_cl.por_favor_verifica_el_recaptcha'));
  }

  if (!checkedAuth.value) {
    return (error.value = t('landing.terrestre.contratacion_cl.terminos_y_condiciones_no_aceptados'));
  }

  if (!isFormValidForm2) {
    return (error.value = t('landing.terrestre.contratacion_cl.el_formulario_debe_estar_completo'));
  }
  if (!isFormValid) {
    return (error.value = t('landing.terrestre.contratacion_cl.el_formulario_debe_estar_completo'));
  }

  const {
    address,
    companyGPSId,
    email,
    phone1,
    tradeName,
    comuna,
    region,
    postCode,
  } = itemsForm2;

  const {
    originComuna,
    typeOfMerchandise,
    destinationComuna,
    indemnificationLimit,
    truckPlate,
    trailerPlate,
    driverName,
    documentDescription,
    driverCellphone,
    driverEmail,
    startDate,
    endDate,
    startTime,
    endTime,
    stops,
    stop1,
    stop2,
    stop3,
    document,
    originRegion,
    destinationRegion,
  } = items;

  const isOkDepartureDateTime = wrongDepartureDateTime(startDate, startTime, 'cl');

  if (isOkDepartureDateTime) {
    return (error.value =
      t('landing.terrestre.contratacion_cl.la_hora_de_salida_no_puede_ser_menor'));
  }

  try {
    loading.value = true;
    const rutWithoutSpecialChars = store.client.rut.replace(/[-.]/g, '');

    const createOrUpdateClientData = {
      tradeName,
      address,
      email,
      phone1,
      location: comuna.nombre,
      postCode,
      rut: rutWithoutSpecialChars,
      regionProvince: region.nombre,
      companyGPSId: store.client?.companyGPS?.id ?? companyGPSId.id,
      urlDomain: window.location.origin,
    };

    const client = await createOrUpdateClient(
      store.client.id,
      createOrUpdateClientData,
      'CL',
    );
    store.setProp({
      key: 'client',
      value: { ...store.client, id: client.data.client.id },
    });

    const travelStartDate = new Date(`${startDate}T${startTime}`);
    const travelEndDate = new Date(`${endDate}T${endTime}`);
    const fechaISOstart = moment(travelStartDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
    const fechaISOend = moment(travelEndDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
    const assetId = store.data.asset.id;
    const subAssetId = store.data.subAsset?.id ?? null;
    const productId = store.data.product?.id ?? null;

    const start = moment(fechaISOstart);
    const end = moment(fechaISOend);

    if (end.isBefore(start)) {
      loading.value = false;
      return (error.value = 'La fecha de fin no puede ser menor a la de inicio');
    }

    const createPolicyData: any = {
      tradeName,
      assetId,
      subAssetId,
      productId,
      phone1,
      typeOfMerchandise,
      indemnificationLimit,
      originRegionProvince: originRegion.nombre,
      destinationRegionProvince: destinationRegion.nombre,
      originLocation: originComuna.nombre ?? originComuna,
      destinationLocation: destinationComuna.nombre ?? destinationComuna,
      stop1,
      stop2,
      stop3,
      trailerPlate,
      truckPlate,
      driverName,
      documentDescription,
      driverCellphone,
      driverEmail,
      travelStartDate: fechaISOstart,
      travelEndDate: fechaISOend,
      hasStops: stops.nombre,
      companyGPSId: companyGPSId.id,
      document: document?.length ? document[0].image : null,
      clientName: tradeName,
      tributaryCode: formatterRUT(store.client.rut),
      address,
      postalCode: postCode,
      location: comuna.nombre,
      regionProvince: region.nombre,
      email,
      endPrice: finalCalculation?.value?.primaTotal,
      token: tokenCaptcha.value,
      corredorId: store.corredor?.code ?? null,
    };

    const { data } = await createPolicy(
      client.data.client.id,
      client.data.manager.id,
      createPolicyData,
      'CL'
    );

    loading.value = false;

    if (data?.urlPayment) {
      window.location.replace(data?.urlPayment)
    } else {
      error.value = 'Ruta de pago inválida o inexistente'
    }
  } catch (e: any) {
    tokenCaptcha.value = null;
    error.value = e.message || t('global.ocurrio_un_error');
    loading.value = false;
  }finally{
	 captchaRef.value?.reset()
  }
};

const hideModal = () => {
  isOpenModal.value = false;
};

const openCondiciones = () => {
  const w = 900;
  const h = 800;
  const left = window.screenX + (window.outerWidth - w) / 2;
  const top = window.screenY + (window.outerHeight - h) / 2;
  window.open(
    '/garantias-expresas-contempora',
    'condicionesContempora',
    `width=${w},height=${h},left=${left},top=${top},scrollbars=yes,resizable=yes`,
  );
};

const hideModalFinish = () => {
  isOpenModalFinish.value = false;
};
const handleTokenCaptcha = (token: string) => {
  tokenCaptcha.value = token;
};

const handleOnExpired = () => {
  tokenCaptcha.value = null;
};
</script>

<template>
  <div class="policy-client-chile home-contempora">
    <h1 class="landing__title">{{ t('landing.terrestre.contratacion_cl.seguro_de_mercancias_por_trayecto') }}</h1>
    <h2 class="landing__subtitle">{{ t('landing.terrestre.contratacion_cl.ya_falta_menos') }}</h2>
    <h2 class="landing__subtitle">{{ t('landing.terrestre.contratacion_cl.ayudanos_completando') }}</h2>
    <div class="contratacion">
      <div class="contratacion__container">
        <h2 class="text-center text-white">{{ t('landing.terrestre.contratacion_cl.informacion_del_cliente') }}</h2>
        <Form ref="refForm2" :inputs="responsiveFieldsForm2" @select="handleGetCities"></Form>
        <Form ref="refForm3" :inputs="responsiveFieldsForm3" @onSubmit="handleOnSubmitPolicies" @datePicker="handleAllowEndDate"
          @select="handleSelectChange">
          <template #title>
            <h2 class="text-center text-white mt-5">{{ t('landing.terrestre.contratacion_cl.datos_del_viaje') }}</h2>
          </template>
          <template #endForm>
            <h2 class="text-white fs-5">
              {{ t('landing.terrestre.contratacion_cl.terminos_y_condiciones') }}
            </h2>
            <b-form-checkbox v-model="checkedAuth" name="check-button" switch>
              <p class="text-white">
                {{ t('landing.terrestre.contratacion_cl.terminos_contempora') }}
                <span @click="openCondiciones" class="text-decoration-underline cursor-pointer">{{
                  t('landing.terrestre.contratacion_cl.condiciones_generales_particulares') }}</span>
                {{ t('landing.terrestre.contratacion_cl.terminos_contempora_2') }}
              </p>
            </b-form-checkbox>
          </template>
          <template #finalCalculation>
            <div v-if="finalCalculation" class="container p-0 mt-2">
              <b-card class="rounded-4">
                <h2 class="text-center text-primary">
                  {{ t('landing.terrestre.contratacion_cl.calculo_de_prima_fina_y_pago') }}
                </h2>
                <b-row class="mt-3">
                  <b-col>
                    <b-list-group>
                      <b-list-group-item class="d-flex justify-content-between align-items-center">
                        <span>{{ t('landing.terrestre.contratacion_cl.prima_neta') }}</span>
                        <span class="font-weight-bold">{{ `${store.currencyType.simbol} ${finalCalculation.primaNeta}`
                          }}</span>
                      </b-list-group-item>
                      <b-list-group-item class="d-flex justify-content-between align-items-center">
                        <span>{{ t('landing.terrestre.contratacion_cl.iva') }}</span>
                        <span class="font-weight-bold">{{ `${store.currencyType.simbol} ${finalCalculation.IVA}`
                          }}</span>
                      </b-list-group-item>
                      <b-list-group-item
                        class="d-flex justify-content-between align-items-center bg-primary text-white">
                        <span><strong>{{ t('landing.terrestre.contratacion_cl.prima_total') }}</strong></span>
                        <span class="font-weight-bold">{{ `${store.currencyType.simbol} ${finalCalculation.primaTotal}`
                          }}</span>
                      </b-list-group-item>
                    </b-list-group>
                  </b-col>
                </b-row>
              </b-card>
            </div>
          </template>

        <template #buttons>
          <p v-if="error" class="msg-error">{{ error }}</p>
          <CaptchaCloudflare
		     ref="captchaRef"
			 @verified="handleTokenCaptcha"
			 @expired="handleOnExpired"
		  />
          <div class="d-flex">
            <CButton class="p-4 w-100 my-2 px-2" background="background-green" style="font-size: 1.5rem">
              <template #content>
                <div>
                  <BSpinner v-if="loading" />
                  <span v-else>{{ t('landing.terrestre.contratacion_cl.pagar_el_seguro') }}</span>
                </div>
              </template>
            </CButton>
          </div>
        </template>
      </Form>
      </div>
    </div>
    <Footer />
  </div>
  <BModal :modelValue="isOpenModal" hide-footer static @hide="hideModal" size="lg" v-if="policyId">
    <TyC />
  </BModal>
  <BModal :modelValue="isOpenModalFinish" hide-footer static @hide="hideModalFinish" size="lg">
    <h4>Felicitaciones tu poliza a sido creada</h4>
  </BModal>
</template>
